import { useState, useEffect } from 'react'
import {
  Wrench,
  Plane,
  Clock,
  Calendar,
  Plus,
  ChevronRight,
  Settings,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ClipboardList,
  Cog,
  Gauge,
  Cpu,
  Info,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import api from '@/services/api'

// ── Interfaces ──────────────────────────────────────────────────────────────

interface PhaseTask {
  task: string
  ataChapter: string
  estimatedHours: number
  keyParts: string[]
}

interface MaintenancePhase {
  phase: number
  hourRange: string
  title: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
  borderColor: string
  tasks: PhaseTask[]
  totalManhours: number
}

type HondaJetModel = 'HA-420 Classic' | 'HA-420 Elite' | 'HA-420 Elite S' | 'HA-420 Elite II'

interface FleetAircraft {
  id: string
  tailNumber: string
  model: HondaJetModel
  currentHours: number
  currentCycles: number
  currentPhase: number
  hoursUntilNextPhase: number
  serviceCenter: string
  status: 'on-schedule' | 'due-soon' | 'overdue'
}

interface ScheduledEvent {
  id: string
  tailNumber: string
  phase: number
  eventType: string
  scheduledDate: string
  serviceCenter: string
  estimatedDays: number
  status: 'scheduled' | 'in-progress' | 'completed'
}

type ProgramMode = 'phased' | 'traditional'

// ── Phase Data ──────────────────────────────────────────────────────────────

const maintenancePhases: MaintenancePhase[] = [
  {
    phase: 1,
    hourRange: '0 – 150 hrs',
    title: 'Airframe & Flight Controls',
    description: 'Airframe inspections, flight control rigging checks, and structural surveys per ALIM Chapter 5.',
    icon: ClipboardList,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    totalManhours: 48,
    tasks: [
      { task: 'Airframe visual inspection (fuselage, wings, empennage)', ataChapter: 'ATA 51/53/55', estimatedHours: 8, keyParts: ['Composite repair kits', 'Sealant PR-1776'] },
      { task: 'Flight control rigging and free-play check', ataChapter: 'ATA 27', estimatedHours: 10, keyParts: ['Rigging pins HA-420-27-001', 'Tensiometer'] },
      { task: 'Flap and slat system operational test', ataChapter: 'ATA 27-50', estimatedHours: 6, keyParts: ['Flap actuator seals', 'Position sensor assemblies'] },
      { task: 'Spoiler and speed brake inspection', ataChapter: 'ATA 27-60', estimatedHours: 4, keyParts: ['Spoiler hinge bearings'] },
      { task: 'Window and windshield inspection', ataChapter: 'ATA 56', estimatedHours: 6, keyParts: ['Windshield heater elements'] },
      { task: 'Door and emergency exit operational check', ataChapter: 'ATA 52', estimatedHours: 4, keyParts: ['Door seal assemblies', 'Latch mechanisms'] },
      { task: 'Corrosion prevention and control (CPCP) inspection', ataChapter: 'ATA 04', estimatedHours: 10, keyParts: ['Corrosion inhibiting compound CIC-3'] },
    ],
  },
  {
    phase: 2,
    hourRange: '150 – 300 hrs',
    title: 'Engine & Powerplant (GE Honda HF120)',
    description: 'GE Honda HF120 turbofan engine and fuel system inspections per ALIM Chapter 7.',
    icon: Cog,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    totalManhours: 56,
    tasks: [
      { task: 'GE Honda HF120 engine borescope inspection (HPT/LPT)', ataChapter: 'ATA 72', estimatedHours: 12, keyParts: ['Borescope probe tip HA-BSI-01', 'Engine wash kit'] },
      { task: 'Engine oil system service and analysis', ataChapter: 'ATA 79', estimatedHours: 6, keyParts: ['Oil filter P/N HF120-79-010', 'Mobil Jet Oil II'] },
      { task: 'FADEC system functional test', ataChapter: 'ATA 73', estimatedHours: 8, keyParts: ['FADEC test harness', 'EEC software loader'] },
      { task: 'Fuel system inspection and leak check', ataChapter: 'ATA 28', estimatedHours: 10, keyParts: ['Fuel filter elements', 'Fuel boost pump seals'] },
      { task: 'Engine mount and pylon inspection', ataChapter: 'ATA 71', estimatedHours: 8, keyParts: ['Over-the-wing pylon mount bolts', 'Vibration isolators'] },
      { task: 'Nacelle and cowling inspection', ataChapter: 'ATA 54', estimatedHours: 6, keyParts: ['Cowling latches', 'Fire seal assemblies'] },
      { task: 'Thrust reverser operational check', ataChapter: 'ATA 78', estimatedHours: 6, keyParts: ['Thrust reverser actuator seals', 'Blocker door bushings'] },
      { task: 'Engine vibration monitoring data review', ataChapter: 'ATA 77', estimatedHours: 8, keyParts: ['Accelerometer calibration kit'] },
    ],
  },
  {
    phase: 3,
    hourRange: '300 – 450 hrs',
    title: 'Landing Gear, Hydraulics & Pneumatics',
    description: 'Landing gear system, hydraulic power, and pneumatic system inspections per ALIM Chapter 6.',
    icon: Gauge,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    totalManhours: 52,
    tasks: [
      { task: 'Main landing gear detailed inspection', ataChapter: 'ATA 32-10', estimatedHours: 10, keyParts: ['MLG trunnion bearings', 'Brake assembly P/N HA-420-32-050'] },
      { task: 'Nose landing gear and steering check', ataChapter: 'ATA 32-20', estimatedHours: 8, keyParts: ['NLG steering actuator', 'Shimmy damper'] },
      { task: 'Brake and anti-skid system test', ataChapter: 'ATA 32-40', estimatedHours: 6, keyParts: ['Brake pads', 'Anti-skid control box'] },
      { task: 'Tire inspection and pressure check', ataChapter: 'ATA 32-45', estimatedHours: 4, keyParts: ['Main tires P/N 301-442-100', 'Nose tire P/N 301-442-050'] },
      { task: 'Hydraulic system fluid service and leak check', ataChapter: 'ATA 29', estimatedHours: 8, keyParts: ['Skydrol LD-4 hydraulic fluid', 'Hydraulic filter elements'] },
      { task: 'Hydraulic pump and accumulator inspection', ataChapter: 'ATA 29-10', estimatedHours: 8, keyParts: ['Hydraulic pump seals', 'Accumulator pre-charge kit'] },
      { task: 'Pneumatic system and bleed air inspection', ataChapter: 'ATA 36', estimatedHours: 6, keyParts: ['Bleed air duct clamps', 'Pressure regulating valve'] },
      { task: 'Emergency / alternate extension test', ataChapter: 'ATA 32-30', estimatedHours: 6, keyParts: ['Emergency extension handle', 'Free-fall valve'] },
    ],
  },
  {
    phase: 4,
    hourRange: '450 – 600 hrs',
    title: 'Avionics, Electrical & Completion',
    description: 'Garmin G3000 avionics suite, electrical systems, and program completion items per ALIM Chapter 8.',
    icon: Cpu,
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    totalManhours: 44,
    tasks: [
      { task: 'Garmin G3000 avionics functional test and software update', ataChapter: 'ATA 34', estimatedHours: 10, keyParts: ['Garmin SD card update', 'GDU display units'] },
      { task: 'Transponder and ADS-B Out verification', ataChapter: 'ATA 34-50', estimatedHours: 4, keyParts: ['GTX transponder', 'ADS-B test equipment'] },
      { task: 'Autopilot and flight director system test', ataChapter: 'ATA 22', estimatedHours: 8, keyParts: ['GFC 700 AFCS', 'Servo actuators'] },
      { task: 'Communication and navigation radio checks', ataChapter: 'ATA 23/34', estimatedHours: 6, keyParts: ['VHF comm/nav antennas', 'GPS antenna'] },
      { task: 'Electrical power generation and distribution check', ataChapter: 'ATA 24', estimatedHours: 8, keyParts: ['Starter-generator brushes', 'Battery P/N HA-420-24-001'] },
      { task: 'Lighting system inspection (interior/exterior)', ataChapter: 'ATA 33', estimatedHours: 4, keyParts: ['LED landing light assemblies', 'Anti-collision strobe units'] },
      { task: 'Cabin pressurization system test', ataChapter: 'ATA 21', estimatedHours: 6, keyParts: ['Outflow valve', 'Cabin pressure controller'] },
      { task: 'Completion items and final operational checks', ataChapter: 'ATA 05', estimatedHours: 6, keyParts: ['Maintenance release forms', 'Return-to-service checklist'] },
    ],
  },
]

// ── Fleet Sample Data ───────────────────────────────────────────────────────

const sampleFleet: FleetAircraft[] = [
  { id: '1', tailNumber: 'N420HJ', model: 'HA-420 Elite S', currentHours: 487.3, currentCycles: 612, currentPhase: 4, hoursUntilNextPhase: 112.7, serviceCenter: 'Honda Aircraft Company — Greensboro, NC', status: 'on-schedule' },
  { id: '2', tailNumber: 'N421HA', model: 'HA-420 Elite II', currentHours: 142.8, currentCycles: 198, currentPhase: 1, hoursUntilNextPhase: 7.2, serviceCenter: 'Honda Aircraft Company — Greensboro, NC', status: 'due-soon' },
  { id: '3', tailNumber: 'N523HE', model: 'HA-420 Elite', currentHours: 291.4, currentCycles: 345, currentPhase: 2, hoursUntilNextPhase: 8.6, serviceCenter: 'Cutter Aviation — Phoenix, AZ', status: 'due-soon' },
  { id: '4', tailNumber: 'N600HC', model: 'HA-420 Classic', currentHours: 1187.0, currentCycles: 1450, currentPhase: 3, hoursUntilNextPhase: 63.0, serviceCenter: 'Banyan Air Service — Fort Lauderdale, FL', status: 'on-schedule' },
  { id: '5', tailNumber: 'N789HJ', model: 'HA-420 Elite S', currentHours: 605.2, currentCycles: 740, currentPhase: 1, hoursUntilNextPhase: -5.2, serviceCenter: 'West Star Aviation — East Alton, IL', status: 'overdue' },
]

const sampleSchedule: ScheduledEvent[] = [
  { id: '1', tailNumber: 'N421HA', phase: 2, eventType: 'Phase 2 Inspection', scheduledDate: '2026-03-05', serviceCenter: 'Honda Aircraft Company — Greensboro, NC', estimatedDays: 5, status: 'scheduled' },
  { id: '2', tailNumber: 'N523HE', phase: 3, eventType: 'Phase 3 Inspection', scheduledDate: '2026-03-12', serviceCenter: 'Cutter Aviation — Phoenix, AZ', estimatedDays: 4, status: 'scheduled' },
  { id: '3', tailNumber: 'N789HJ', phase: 2, eventType: 'Phase 2 Inspection (Overdue)', scheduledDate: '2026-02-22', serviceCenter: 'West Star Aviation — East Alton, IL', estimatedDays: 5, status: 'in-progress' },
  { id: '4', tailNumber: 'N420HJ', phase: 1, eventType: '600-Hour Major Inspection', scheduledDate: '2026-04-10', serviceCenter: 'Honda Aircraft Company — Greensboro, NC', estimatedDays: 12, status: 'scheduled' },
  { id: '5', tailNumber: 'N600HC', phase: 4, eventType: 'Phase 4 Inspection + 1200-Hr Extended', scheduledDate: '2026-05-01', serviceCenter: 'Banyan Air Service — Fort Lauderdale, FL', estimatedDays: 18, status: 'scheduled' },
]

// Manhours chart data
const manhoursByPhase = [
  { phase: 'Phase 1', manhours: 48, fill: '#3b82f6' },
  { phase: 'Phase 2', manhours: 56, fill: '#10b981' },
  { phase: 'Phase 3', manhours: 52, fill: '#f59e0b' },
  { phase: 'Phase 4', manhours: 44, fill: '#8b5cf6' },
]

// ── Component ───────────────────────────────────────────────────────────────

export function MaintenanceProgramPage() {
  const [activeTab, setActiveTab] = useState('phases')
  const [programMode, setProgramMode] = useState<ProgramMode>('phased')
  const [fleet, setFleet] = useState<FleetAircraft[]>(sampleFleet)
  const [schedule] = useState<ScheduledEvent[]>(sampleSchedule)
  const [loading, setLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newAircraft, setNewAircraft] = useState({
    tailNumber: '',
    model: 'HA-420 Elite S' as HondaJetModel,
    currentHours: '',
    currentCycles: '',
  })

  useEffect(() => {
    const fetchFleet = async () => {
      setLoading(true)
      try {
        const data = await api.get<FleetAircraft[]>('/api/maintenance-program/aircraft')
        if (data && Array.isArray(data)) {
          setFleet(data)
        }
      } catch {
        // Use sample data on API error
      } finally {
        setLoading(false)
      }
    }
    fetchFleet()
  }, [])

  const getPhaseForHours = (hours: number): number => {
    const cycleHours = hours % 600
    if (cycleHours < 150) return 1
    if (cycleHours < 300) return 2
    if (cycleHours < 450) return 3
    return 4
  }

  const handleAddAircraft = async () => {
    if (!newAircraft.tailNumber || !newAircraft.currentHours) return

    const hours = parseFloat(newAircraft.currentHours)
    const cycles = parseInt(newAircraft.currentCycles) || 0
    const phase = getPhaseForHours(hours)
    const phaseStart = (phase - 1) * 150
    const nextPhaseHour = phaseStart + 150
    const hoursUntilNext = nextPhaseHour - (hours % 600)

    const aircraft: FleetAircraft = {
      id: String(fleet.length + 1),
      tailNumber: newAircraft.tailNumber.toUpperCase(),
      model: newAircraft.model,
      currentHours: hours,
      currentCycles: cycles,
      currentPhase: phase,
      hoursUntilNextPhase: parseFloat(hoursUntilNext.toFixed(1)),
      serviceCenter: 'Honda Aircraft Company — Greensboro, NC',
      status: hoursUntilNext <= 10 ? 'due-soon' : 'on-schedule',
    }

    try {
      await api.post('/api/maintenance-program/aircraft', aircraft)
    } catch {
      // Continue with local state update
    }

    setFleet([...fleet, aircraft])
    setNewAircraft({ tailNumber: '', model: 'HA-420 Elite S', currentHours: '', currentCycles: '' })
    setShowAddForm(false)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'on-schedule':
        return <Badge variant="success">On Schedule</Badge>
      case 'due-soon':
        return <Badge variant="warning">Due Soon</Badge>
      case 'overdue':
        return <Badge variant="destructive">Overdue</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const getScheduleStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="info">Scheduled</Badge>
      case 'in-progress':
        return <Badge variant="warning">In Progress</Badge>
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HondaJet HA-420 Maintenance Program</h1>
          <p className="text-gray-600">Phased inspection program per Honda Aircraft Company ALIM</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border bg-muted p-1">
            <button
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                programMode === 'phased' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setProgramMode('phased')}
            >
              Phased Program
            </button>
            <button
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                programMode === 'traditional' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setProgramMode('traditional')}
            >
              Traditional 600/1200 hr
            </button>
          </div>
        </div>
      </div>

      {/* ALIM Revision Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-blue-600 shrink-0" />
            <div>
              <p className="font-medium text-blue-900">ALIM Revision J (Aug 2024)</p>
              <p className="text-sm text-blue-700">
                Reduced manhours at 600-hour interval. Updated phase task cards reflect revised inspection requirements.
                {programMode === 'traditional' && ' Traditional program follows 600-hour and 1200-hour major inspection intervals.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Program Mode Notice for Traditional */}
      {programMode === 'traditional' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 shrink-0" />
              <div>
                <p className="font-medium text-amber-900">Traditional Program Selected</p>
                <p className="text-sm text-amber-700">
                  The traditional program combines all phase inspections at 600-hour and 1200-hour intervals.
                  Total estimated manhours at 600-hr: 220 hrs. At 1200-hr extended: 340+ hrs.
                  Honda Aircraft Company recommends the phased program for reduced downtime.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="phases" className="gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Phase View</span>
            <span className="sm:hidden">Phases</span>
          </TabsTrigger>
          <TabsTrigger value="fleet" className="gap-2">
            <Plane className="h-4 w-4" />
            <span className="hidden sm:inline">Fleet Status</span>
            <span className="sm:hidden">Fleet</span>
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2">
            <Calendar className="h-4 w-4" />
            Schedule
          </TabsTrigger>
        </TabsList>

        {/* ── Phase View ─────────────────────────────────────────────────── */}
        <TabsContent value="phases">
          <div className="space-y-6">
            {/* Manhours Overview Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Estimated Manhours by Phase</CardTitle>
                <CardDescription>
                  Total program cycle: {maintenancePhases.reduce((sum, p) => sum + p.totalManhours, 0)} manhours across 600-hour interval
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={manhoursByPhase}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                      <XAxis dataKey="phase" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1a365d',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                        }}
                        formatter={(value: number) => [`${value} hrs`, 'Manhours']}
                      />
                      <Bar dataKey="manhours" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Phase Cards */}
            <div className="grid gap-6 lg:grid-cols-2">
              {maintenancePhases.map((phase) => {
                const PhaseIcon = phase.icon
                return (
                  <Card key={phase.phase} className={`border-2 ${phase.borderColor}`}>
                    <CardHeader className={phase.bgColor}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`rounded-lg p-2 bg-white/80 ${phase.color}`}>
                            <PhaseIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">Phase {phase.phase}</CardTitle>
                            <CardDescription className={phase.color}>{phase.hourRange}</CardDescription>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono">
                          {phase.totalManhours} MH
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="mb-4 text-sm text-gray-600">{phase.description}</p>
                      <ScrollArea className="h-[320px] pr-3">
                        <div className="space-y-3">
                          {phase.tasks.map((task, idx) => (
                            <div key={idx} className="rounded-lg border p-3">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900">{task.task}</p>
                                <Badge variant="secondary" className="shrink-0 font-mono text-xs">
                                  {task.estimatedHours}h
                                </Badge>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">{task.ataChapter}</p>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {task.keyParts.map((part, pIdx) => (
                                  <span
                                    key={pIdx}
                                    className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                                  >
                                    {part}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                    <CardFooter className="text-xs text-gray-500 border-t pt-4">
                      ATA Chapters: {[...new Set(phase.tasks.map((t) => t.ataChapter))].join(' | ')}
                    </CardFooter>
                  </Card>
                )
              })}
            </div>

            {/* Major Inspection Notes */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <p className="font-medium text-red-900">600-Hour Major Inspection</p>
                      <p className="text-sm text-red-700">
                        Combines all four phase inspections plus additional structural and NDT requirements.
                        Estimated 220 manhours. Landing gear overhaul assessment required.
                        Recommend scheduling at Honda Aircraft Company Greensboro or an HACI-authorized service center.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-orange-600 shrink-0" />
                    <div>
                      <p className="font-medium text-orange-900">1200-Hour Extended Inspection</p>
                      <p className="text-sm text-orange-700">
                        Full 600-hr scope plus extended structural inspections, engine hot section review,
                        landing gear overhaul, and avionics database revalidation.
                        Estimated 340+ manhours. Engine on-condition assessment per GE Honda CSP.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ── Fleet Status ───────────────────────────────────────────────── */}
        <TabsContent value="fleet">
          <div className="space-y-6">
            {/* Fleet Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Aircraft</p>
                      <p className="text-2xl font-bold">{fleet.length}</p>
                    </div>
                    <Plane className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">On Schedule</p>
                      <p className="text-2xl font-bold text-green-600">
                        {fleet.filter((a) => a.status === 'on-schedule').length}
                      </p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Due Soon</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {fleet.filter((a) => a.status === 'due-soon').length}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Overdue</p>
                      <p className="text-2xl font-bold text-red-600">
                        {fleet.filter((a) => a.status === 'overdue').length}
                      </p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Add Aircraft Button */}
            <div className="flex justify-end">
              <Button onClick={() => setShowAddForm(!showAddForm)}>
                <Plus className="mr-2 h-4 w-4" />
                {showAddForm ? 'Cancel' : 'Add / Update Aircraft'}
              </Button>
            </div>

            {/* Add Aircraft Form */}
            {showAddForm && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Add / Update Aircraft</CardTitle>
                  <CardDescription>Enter HondaJet fleet aircraft details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Tail Number</label>
                      <Input
                        placeholder="N420HJ"
                        value={newAircraft.tailNumber}
                        onChange={(e) => setNewAircraft({ ...newAircraft, tailNumber: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Model</label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={newAircraft.model}
                        onChange={(e) => setNewAircraft({ ...newAircraft, model: e.target.value as HondaJetModel })}
                      >
                        <option value="HA-420 Classic">HA-420 Classic</option>
                        <option value="HA-420 Elite">HA-420 Elite</option>
                        <option value="HA-420 Elite S">HA-420 Elite S</option>
                        <option value="HA-420 Elite II">HA-420 Elite II</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Current Hours</label>
                      <Input
                        type="number"
                        placeholder="487.3"
                        value={newAircraft.currentHours}
                        onChange={(e) => setNewAircraft({ ...newAircraft, currentHours: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">Current Cycles</label>
                      <Input
                        type="number"
                        placeholder="612"
                        value={newAircraft.currentCycles}
                        onChange={(e) => setNewAircraft({ ...newAircraft, currentCycles: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={handleAddAircraft}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Aircraft
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fleet Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Fleet Aircraft</CardTitle>
                <CardDescription>
                  {programMode === 'phased' ? 'Phased maintenance program status' : 'Traditional 600/1200 hr program status'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading fleet data...</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Tail Number</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Model</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Hours</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Cycles</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">Current Phase</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Hrs to Next Phase</th>
                          <th className="hidden px-4 py-3 text-left font-medium text-gray-600 lg:table-cell">Service Center</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fleet.map((aircraft) => (
                          <tr key={aircraft.id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-mono font-semibold text-gray-900">{aircraft.tailNumber}</td>
                            <td className="px-4 py-3 text-gray-700">{aircraft.model}</td>
                            <td className="px-4 py-3 text-right font-mono">{aircraft.currentHours.toFixed(1)}</td>
                            <td className="px-4 py-3 text-right font-mono">{aircraft.currentCycles.toLocaleString()}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline">Phase {aircraft.currentPhase}</Badge>
                            </td>
                            <td className={`px-4 py-3 text-right font-mono ${
                              aircraft.hoursUntilNextPhase <= 0 ? 'text-red-600 font-bold' :
                              aircraft.hoursUntilNextPhase <= 10 ? 'text-yellow-600 font-semibold' :
                              'text-gray-700'
                            }`}>
                              {aircraft.hoursUntilNextPhase.toFixed(1)}
                            </td>
                            <td className="hidden px-4 py-3 text-gray-600 lg:table-cell">{aircraft.serviceCenter}</td>
                            <td className="px-4 py-3 text-center">{getStatusBadge(aircraft.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Schedule ────────────────────────────────────────────────────── */}
        <TabsContent value="schedule">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Upcoming Maintenance Schedule
                </CardTitle>
                <CardDescription>
                  Timeline of scheduled maintenance events across the fleet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {schedule
                    .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                    .map((event) => {
                      const eventDate = new Date(event.scheduledDate)
                      const today = new Date()
                      const daysUntil = Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                      const phaseData = maintenancePhases.find((p) => p.phase === event.phase)

                      return (
                        <div
                          key={event.id}
                          className={`rounded-lg border-l-4 border p-4 ${
                            event.status === 'in-progress'
                              ? 'border-l-yellow-500 bg-yellow-50'
                              : daysUntil < 0
                              ? 'border-l-red-500 bg-red-50'
                              : daysUntil <= 14
                              ? 'border-l-amber-500 bg-amber-50'
                              : 'border-l-blue-500 bg-white'
                          }`}
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                              <div className="flex flex-col items-center rounded-lg bg-white p-2 shadow-sm min-w-[60px]">
                                <span className="text-xs text-gray-500">
                                  {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                                </span>
                                <span className="text-xl font-bold text-gray-900">
                                  {eventDate.getDate()}
                                </span>
                                <span className="text-xs text-gray-500">{eventDate.getFullYear()}</span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{event.eventType}</span>
                                  {getScheduleStatusBadge(event.status)}
                                </div>
                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                  <Plane className="h-3.5 w-3.5" />
                                  <span className="font-mono font-medium">{event.tailNumber}</span>
                                  <span className="text-gray-400">|</span>
                                  <Wrench className="h-3.5 w-3.5" />
                                  <span>{event.serviceCenter}</span>
                                </div>
                                {phaseData && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    {phaseData.title} — Est. {phaseData.totalManhours} manhours
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-600">{event.estimatedDays} days</span>
                              </div>
                              <Button size="sm" variant="outline">
                                Details
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
